import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, BarChart3, Users, ArrowRight, Map as MapIcon, Lock, Sparkles, ExternalLink, FileText } from "lucide-react";
import api from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";

const HM_META = {
  top_growth: { icon: TrendingUp, label: "Fastest-growing", color: "emerald", fmt: (c) => `+${c.yoy_change_pct}%` },
  top_volume: { icon: BarChart3, label: "Highest volume", color: "slate", fmt: (c) => c.permits_2023.total_bldgs.toLocaleString() + " permits" },
  top_per_capita: { icon: Users, label: "Per capita", color: "amber", fmt: (c) => c.permits_per_1k.toFixed(2) + " / 1k" },
};

function GateCTA({ remainingByMetric, metricKey, isAuthed }) {
  const remain = (remainingByMetric?.[metricKey] || 0) - 5;
  const proCta = isAuthed
    ? { label: "Upgrade to Pro", href: "/pricing", icon: Sparkles, sub: "From $29/mo · 7-day free trial included on signup" }
    : { label: "Start 7-day free trial", href: "/register", icon: Sparkles, sub: "No card up front · cancel anytime" };
  return (
    <div
      className="relative col-span-1 md:col-span-2 lg:col-span-3 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 p-8 md:p-10 text-white"
      data-testid={`gate-cta-${metricKey}`}
    >
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(16,185,129,0.6), transparent 40%), radial-gradient(circle at 80% 70%, rgba(99,102,241,0.4), transparent 45%)" }} />
      <div className="relative flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 mb-4">
            <Lock className="w-3 h-3" />
            <span className="text-xs font-mono uppercase tracking-[0.18em]">Locked &middot; {remain > 0 ? `${remain.toLocaleString()} more counties` : "Full dataset"}</span>
          </div>
          <h3 className="font-outfit text-2xl md:text-3xl font-bold tracking-tight">
            Unlock the full ranking + permit detail.
          </h3>
          <p className="mt-3 text-slate-300 text-sm md:text-base leading-relaxed">
            Coast To Coast Permitting Pro opens the complete 25-county list per metric, per-county fee schedules,
            10-year monthly trends, AHJ contact intelligence, and the permit submission tracker.
          </p>
          <p className="mt-2 text-xs text-slate-400 font-mono">{proCta.sub}</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button asChild data-testid={`gate-cta-primary-${metricKey}`} size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 h-11">
            <Link to={proCta.href}>
              <proCta.icon className="w-4 h-4 mr-2" />{proCta.label}
            </Link>
          </Button>
          {!isAuthed && (
            <Button asChild data-testid={`gate-login-${metricKey}`} size="lg" variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white h-11">
              <Link to="/login">Sign in</Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function HotMarketsTabs({ data, isPro, isAuthed }) {
  return (
    <Tabs defaultValue="top_growth" className="mt-8" data-testid="hot-markets-tabs">
      <TabsList className="grid w-full max-w-xl grid-cols-3">
        {Object.entries(HM_META).map(([k, m]) => {
          const Icon = m.icon;
          return (
            <TabsTrigger key={k} value={k} data-testid={`hot-tab-${k}`}>
              <Icon className="w-3.5 h-3.5 mr-1.5" />
              {m.label}
            </TabsTrigger>
          );
        })}
      </TabsList>

      {Object.entries(HM_META).map(([k, m]) => (
        <TabsContent key={k} value={k} className="mt-8">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">{data.metric_labels[k]}</div>
            <div className="text-xs font-mono text-slate-500">
              Showing <strong className="text-slate-900">{data[k].length}</strong> of <strong className="text-slate-900">{data.total_available[k].toLocaleString()}</strong>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`hot-list-${k}`}>
            {data[k].map((c, i) => (
              <Link key={c.fips} to={`/county/${c.fips}`} data-testid={`hot-row-${c.fips}`}
                className="bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-400 hover:shadow-md transition-all group">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-mono text-xs text-slate-400">#{i + 1}</div>
                  <Badge variant="outline" className="font-mono text-[10px]">{c.state_abbr}</Badge>
                </div>
                <div className="font-outfit text-base font-semibold text-slate-900 mb-1">{c.full_name}</div>
                <div className="text-xs text-slate-500 mb-3">{c.state_name} · FIPS {c.fips}</div>
                <div className="flex items-center justify-between">
                  <span className={`font-outfit text-2xl font-bold ${m.color === "emerald" ? "text-emerald-600" : m.color === "amber" ? "text-amber-600" : "text-slate-900"}`}>
                    {m.fmt(c)}
                  </span>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-700 transition-colors" />
                </div>
              </Link>
            ))}
            {!isPro && data.is_preview && (
              <GateCTA remainingByMetric={data.total_available} metricKey={k} isAuthed={isAuthed} />
            )}
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
}

function FeeComparator({ isPro, isAuthed }) {
  const [feeData, setFeeData] = useState(null);
  const [stateFilter, setStateFilter] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    const qs = stateFilter ? `?state=${stateFilter}&limit=30` : "?limit=30";
    api.get(`/public/fee-comparator${qs}`)
      .then((r) => setFeeData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load fees"));
  }, [stateFilter]);

  if (error) return <div className="text-sm text-red-600" data-testid="fee-error">{error}</div>;
  if (!feeData) return <div className="text-slate-500 mt-6" data-testid="fee-loading">Loading fee schedules…</div>;

  const states = Array.from(new Set(feeData.rows.map(r => r.state_abbr))).sort();

  return (
    <div className="mt-6" data-testid="fee-comparator-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-outfit text-xl font-semibold text-slate-900">Curated fee comparator</h3>
          <p className="text-xs text-slate-500 mt-1">
            Showing <strong className="text-slate-900">{feeData.rows.length}</strong> of <strong className="text-slate-900">{feeData.total_available}</strong> curated counties · sourced directly from official permit offices
          </p>
        </div>
        {isPro && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500">State</span>
            <select
              data-testid="fee-state-filter"
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <option value="">All states</option>
              {states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white">
        <table className="w-full text-sm" data-testid="fee-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-[0.12em] text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 sticky left-0 bg-slate-50">County</th>
              {feeData.categories.map((c) => (
                <th key={c.key} className="text-left px-4 py-3 whitespace-nowrap">{c.label}</th>
              ))}
              <th className="text-left px-4 py-3">Source</th>
            </tr>
          </thead>
          <tbody>
            {feeData.rows.map((r) => (
              <tr key={r.fips} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`fee-row-${r.fips}`}>
                <td className="px-4 py-3 sticky left-0 bg-white">
                  <Link to={`/county/${r.fips}`} className="font-medium text-slate-900 hover:text-slate-600">
                    {r.full_name}
                  </Link>
                  <div className="text-xs text-slate-500 font-mono">{r.state_abbr} · {r.permits_2023.toLocaleString()} permits</div>
                </td>
                {feeData.categories.map((c) => {
                  const cell = r[c.key] || {};
                  return (
                    <td key={c.key} className="px-4 py-3 align-top">
                      <div className="font-medium text-slate-900">{cell.fee || <span className="text-slate-400">—</span>}</div>
                      {cell.days && <div className="text-xs text-slate-500 mt-0.5">{cell.days} days</div>}
                    </td>
                  );
                })}
                <td className="px-4 py-3">
                  {r.source_url ? (
                    <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
                      Official <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!isPro && feeData.is_preview && (
        <GateCTA remainingByMetric={{ fee: feeData.total_available }} metricKey="fee" isAuthed={isAuthed} />
      )}
    </div>
  );
}

export default function HotMarkets() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const isAuthed = !!user;
  const isPro = !!user?.subscription?.is_pro;

  useEffect(() => {
    if (loading) return;
    api.get("/public/hot-markets?limit=25").then((r) => setData(r.data));
  }, [loading]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="hot-markets-page">
      {/* Reuse global header when authed, lightweight nav when public */}
      {isAuthed ? (
        <Header />
      ) : (
        <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="h-full max-w-screen-2xl mx-auto px-6 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 group" data-testid="hot-home-link">
              <div className="w-8 h-8 bg-slate-900 rounded-md flex items-center justify-center">
                <MapIcon className="w-4 h-4 text-white" />
              </div>
              <span className="font-outfit font-bold text-slate-900">Coast To Coast Permitting</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button asChild variant="ghost" data-testid="hot-login-link"><Link to="/login">Sign in</Link></Button>
              <Button asChild data-testid="hot-register-link" className="bg-slate-900 hover:bg-slate-800 text-white"><Link to="/register">Get free access</Link></Button>
            </div>
          </div>
        </header>
      )}

      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-mono uppercase tracking-[0.18em] text-emerald-700">Live U.S. Census BPS 2023 data</span>
          </div>
          <h1 className="font-outfit text-5xl md:text-6xl font-bold tracking-tight text-slate-900 leading-tight">
            Where U.S. construction <br />is <span className="text-emerald-600">actually</span> growing.
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">
            Top counties by permit growth, volume, and per-capita density — plus a side-by-side fee comparator across the country&apos;s busiest jurisdictions.
            {!isPro && (isAuthed
              ? " You're seeing the free preview (top 5 per metric). Upgrade to Pro to unlock the full data."
              : " Public preview shows the top 5 per metric; start the 7-day free trial to unlock everything.")}
          </p>
        </div>

        {!data ? (
          <div className="mt-12 text-slate-500" data-testid="hot-loading">Loading…</div>
        ) : (
          <Tabs defaultValue="hot" className="mt-12" data-testid="dashboard-tabs">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="hot" data-testid="dashboard-tab-hot">
                <TrendingUp className="w-4 h-4 mr-2" />Hot markets
              </TabsTrigger>
              <TabsTrigger value="fees" data-testid="dashboard-tab-fees">
                <FileText className="w-4 h-4 mr-2" />Fee comparator
              </TabsTrigger>
            </TabsList>
            <TabsContent value="hot">
              <HotMarketsTabs data={data} isPro={isPro} isAuthed={isAuthed} />
            </TabsContent>
            <TabsContent value="fees">
              <FeeComparator isPro={isPro} isAuthed={isAuthed} />
            </TabsContent>
          </Tabs>
        )}

        {!isPro && (
          <section className="mt-16 bg-slate-900 text-white rounded-2xl p-8 md:p-12" data-testid="hot-footer-cta">
            <h2 className="font-outfit text-3xl md:text-4xl font-semibold tracking-tight max-w-2xl">
              {isAuthed ? "Ready to unlock the full permit picture?" : "Go beyond rankings. Get the full permit picture."}
            </h2>
            <p className="mt-3 text-slate-300 max-w-xl">
              AHJ &middot; Cities &middot; Counties &middot; Townships &middot; Metros &middot; 67k places &middot; curated fee schedules &middot; AHJ contact intel &middot; 10-year trends &middot; permit submission tracker.
            </p>
            <div className="mt-6 flex gap-3 flex-wrap">
              {isAuthed ? (
                <Button asChild data-testid="hot-cta-upgrade" size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 h-12 px-7">
                  <Link to="/pricing">See plans &amp; upgrade</Link>
                </Button>
              ) : (
                <>
                  <Button asChild data-testid="hot-cta-register" size="lg" className="bg-white text-slate-900 hover:bg-slate-100 h-12 px-7">
                    <Link to="/register">Start 7-day free trial</Link>
                  </Button>
                  <Button asChild data-testid="hot-cta-pricing" size="lg" variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-7">
                    <Link to="/pricing">See pricing</Link>
                  </Button>
                </>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
